import { useEffect, useState, useCallback } from 'react';
import { useFetch } from '../hooks/useFetch';
import { API_ENDPOINTS } from './endpoints';
import { Project } from '../types/project';
import projectData from '../mocks/projectData.json';
import { Sample } from '../types/sample';
import { ApiResponse } from '../types/api';

// Get all projects mocks

export const useProjectsMock = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProjects = () => {
      try {
        // Simulate API delay
        setTimeout(() => {
          setProjects(projectData.projects as Project[]);
          setIsLoading(false);
        }, 100);
        console.log(projects)
      } catch {
        setError('Failed to fetch projects');
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  return { projects, isLoading, error };
};

export const useFetchRuns = () => {
  const [samples, setSamples] = useState<Sample[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRuns = useCallback(
    async (
      page: number = 1,
      limit: number = 20,
      orderBy: string = "date",
      orderDir: "asc" | "desc" = "desc"
    ): Promise<ApiResponse<Sample[]>> => {
      setIsLoading(true);
      setError(null);

      try {
        const access = localStorage.getItem("access_token");
        if (!access) throw new Error("No access token found");

        const url = `${import.meta.env.VITE_API_URL}/api/v1/dashboard/runs?page=${page}&limit=${limit}&order_by=${orderBy}&order_dir=${orderDir}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${access}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        const data: Sample[] = await response.json();
        setSamples(data); // update local state for all runs

        return { success: true, data };
      } catch (e) {
        const message = e instanceof Error ? e.message : "Failed to fetch runs";
        setError(message);
        return { success: false, data: null, error: message };
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { samples, fetchRuns, isLoading, error };
};

// ----------------------
// Use Samples for Specific Project
// ----------------------
export const useSamples = (projectId: string) => {
  const [samples, setSamples] = useState<Sample[]>([]);
  const [projectName, setProjectName] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { fetchRuns } = useFetchRuns();

  const fetchProjectSamples = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchRuns(); // fetch all runs
      if (response.success && response.data) {
        // Filter samples by projectId
        const projectSamples = response.data;

        setSamples(projectSamples);
      } else {
        setError(response.error || "No samples found for this project");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch samples");
    } finally {
      setIsLoading(false);
    }
  }, [projectId, fetchRuns]);

  useEffect(() => {
    fetchProjectSamples();
  }, [fetchProjectSamples]);

  return { samples, projectName, isLoading, error, refetch: fetchProjectSamples };
};


// Get all projects
export const useProjects = () => {
  const { data, isLoading, error } = useFetch<Project[]>(
    API_ENDPOINTS.PROJECTS
  );
  return { projects: data || [], isLoading, error };
};

// Get single project
export const useProject = (id: string) => {
  const { data, isLoading, error } = useFetch<Project>(
    API_ENDPOINTS.PROJECT_BY_ID(id)
  );
  return { project: data, isLoading, error };
};

// Create project
export const useCreateProject = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createProject = async (
    projectData: Omit<Project, 'id'>
  ): Promise<ApiResponse<Project>> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(API_ENDPOINTS.CREATE_PROJECT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(projectData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create project';
      setError(errorMessage);
      return { success: false, data: null, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return { createProject, isLoading, error };
};

// Update project
export const useUpdateProject = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateProject = async (
    id: string,
    projectData: Partial<Project>
  ): Promise<ApiResponse<Project>> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(API_ENDPOINTS.UPDATE_PROJECT(id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(projectData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update project';
      setError(errorMessage);
      return { success: false, data: null, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return { updateProject, isLoading, error };
};

// Delete project
export const useDeleteProject = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteProject = async (id: string): Promise<ApiResponse<boolean>> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(API_ENDPOINTS.DELETE_PROJECT(id), {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      return { success: true, data: true };
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to delete project';
      setError(errorMessage);
      return { success: false, data: null, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return { deleteProject, isLoading, error };
};

export const useChangePassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changePassword = async (
    oldPassword: string,
    newPassword: string
  ): Promise<ApiResponse<null>> => {
    setIsLoading(true);
    setError(null);
    try {
      const access = localStorage.getItem('access_token');
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/auth/change-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${access}`,
          },
          body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
        }
      );
      if (!response.ok) throw new Error(`HTTP status ${response.status}`);
      return { success: true, data: null };
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to change password';
      setError(message);
      return { success: false, data: null, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  return { changePassword, isLoading, error };
};

export const useChangeProfilePhoto = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changeProfilePhoto = async (
    formData: FormData
  ): Promise<ApiResponse<{ url: string }>> => {
    setIsLoading(true);
    setError(null);
    try {
      const access = localStorage.getItem('access_token');
      //const formData = new FormData();
      //formData.append('photo', photoFile);
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/auth/user/upload-photo`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${access}` },
          body: formData,
        }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP status ${response.status}: ${errorText}`);
      }
      const data = await response.json();
      if (!data.url) throw new Error('No photo URL returned');
      // return { success: true, data };
      return { data, error: null };
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to upload photo';
      setError(message);
      return { data: null, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  return { changeProfilePhoto, isLoading, error };
};
